import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { useDocuments, useSettings } from "@/hooks/useData";
import { DOC_TYPES, fmtDate, fmtMoney } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const empty = "https://images.unsplash.com/photo-1763900854080-f6b9dda1442a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Documents() {
  const [params, setParams] = useSearchParams();
  const type = params.get("type") || "";
  const [q, setQ] = useState("");
  const { data: docs = [], isLoading } = useDocuments({ type: type || undefined, q: q || undefined });
  const { data: settings } = useSettings();

  return (
    <div className="rise space-y-8" data-testid="documents-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <p className="overline mb-3">Archive</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Documents</h1>
        </div>
        <label className="relative w-full lg:w-80">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
          <input className="field pl-9" placeholder="Search number, client, title" value={q} data-testid="documents-search" onChange={(e) => setQ(e.target.value)} />
        </label>
      </div>

      <div className="flex border border-border w-fit max-w-full overflow-x-auto">
        {[["", "All"], ...Object.entries(DOC_TYPES).map(([k, v]) => [k, v.plural])].map(([k, l]) => (
          <button key={k} onClick={() => setParams(k ? { type: k } : {})} data-testid={`filter-${k || "all"}`} className={`h-10 px-4 text-sm font-medium whitespace-nowrap border-r border-border last:border-0 transition-colors ${type === k ? "bg-foreground text-background" : "hover:bg-muted"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="panel overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 overline border-b border-border">
          <div className="col-span-2">Number</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-3">Party</div>
          <div className="col-span-2">Issued</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2 text-right">Total</div>
        </div>
        {isLoading ? (
          <div className="p-12 text-center overline animate-pulse">Loading</div>
        ) : docs.length === 0 ? (
          <div className="relative p-16 text-center overflow-hidden" data-testid="documents-empty">
            <img src={empty} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
            <div className="relative">
              <h3 className="font-display text-2xl font-bold mb-2">No documents found</h3>
              <p className="text-muted-foreground mb-6">Try another filter, or create something new.</p>
              <Link to={`/documents/new${type ? `?type=${type}` : ""}`} data-testid="empty-create-button" className="btn-press inline-flex h-10 px-5 items-center bg-primary text-primary-foreground font-semibold text-sm">Create {type ? DOC_TYPES[type].label : "document"}</Link>
            </div>
          </div>
        ) : (
          <ul>
            {docs.map((d) => (
              <li key={d.id} className="border-b border-border/40 last:border-0">
                <Link to={`/documents/${d.id}`} data-testid={`doc-row-${d.id}`} className="grid grid-cols-12 gap-2 md:gap-4 items-center px-6 py-4 hover:bg-muted transition-colors">
                  <div className="col-span-6 md:col-span-2 font-mono text-sm">{d.number}</div>
                  <div className="col-span-6 md:col-span-2 text-xs text-muted-foreground text-right md:text-left">{DOC_TYPES[d.type].label}</div>
                  <div className="col-span-6 md:col-span-3 text-sm truncate">{d.party?.name || "—"}</div>
                  <div className="hidden md:block col-span-2 text-xs text-muted-foreground">{fmtDate(d.issue_date)}</div>
                  <div className="col-span-3 md:col-span-1"><StatusBadge status={d.status} /></div>
                  <div className="col-span-3 md:col-span-2 text-right font-mono text-sm">{fmtMoney(d.total, settings)}</div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
